import React, { PropsWithChildren, useEffect, useRef, useState } from "react";
import { getAPIBaseURL } from "../api/api";
import useGetOrganization from "../api/useGetOrganization";
import { useSessionContext } from "supertokens-auth-react/recipe/session";

interface WSContextType {
  socket: WebSocket | null;
  lastMessage: string | null;
}

export const WebSocketContext = React.createContext<WSContextType>({
  socket: null,
  lastMessage: null,
});

export const WebSocketProvider = ({ children }: PropsWithChildren<{}>) => {
  const sessionContext = useSessionContext() as any;
  const { userId } = sessionContext;
  const [socket, setSocket] = useState<WebSocket | null>(null);
  const [lastMessage, setLastMessage] = useState<string | null>(null);
  const connectingRef = useRef(false);
  const { data: organization } = useGetOrganization();
  const workspaceId = organization?.workspaces?.[0]?.id || "";
  const debug = false;

  const connect = () => {
    const wsURL = getAPIBaseURL("v1").replace(/^http/, "ws");
    if (debug) console.log("WS connection opening", wsURL);
    const socketConnection = new WebSocket(`${wsURL}ws?workspaceId=${workspaceId}&userId=${userId}`);

    socketConnection.onopen = (event) => {
      if (debug) console.log("WS connection opened", event);
    };
    socketConnection.onerror = (event) => {
      if (debug) console.error("WS connection error", event);
    };
    socketConnection.onclose = (event) => {
      if (debug) console.log("WS connection closed", event);
      setSocket(null);
      connectingRef.current = false;

      // Attempt to reconnect every 10 seconds
      if (!connectingRef.current) {
        setTimeout(connect, 10000);
      }
    };

    socketConnection.onmessage = (event) => {
      if (debug) console.log("WS message received", event);
      setLastMessage(event.data);
    };

    setSocket(socketConnection);
  };

  useEffect(() => {
    if (workspaceId && userId && !connectingRef.current && !socket) {
      connectingRef.current = true;
      connect();
      return () => {
        if (socket && (socket as WebSocket).readyState === WebSocket.OPEN) {
          if (debug) console.log("WS connection closing");
          (socket as WebSocket).close();
        }
      };
    }
  }, [workspaceId, userId]);

  if (!userId || !workspaceId) {
    return <>{children}</>;
  }

  return <WebSocketContext.Provider value={{ socket, lastMessage }}>{children}</WebSocketContext.Provider>;
};
